"use client";

import React, { useEffect, useRef, useState } from "react";
import { WidgetTemplateConfig } from "@/modules/widget-templates";
import CompactTickerCard from "@/app/widgets/tickers/CompactTickerCard";
import { BarChart } from "@/app/widgets/chart/BarChart";
import { LineChart } from "@/app/widgets/chart/LineChart";
import PieChart from "@/app/widgets/chart/PieChart";
import Funnel from "@/app/widgets/chart/Funnel";
import BubbleChart from "@/app/widgets/chart/BubbleChart";
import { InfoIcon } from "@/components/icons";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MiniWidgetPreviewProps {
    widgetConfig: WidgetTemplateConfig;
}

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

const TEAL = "#079a95";
const TEAL_LIGHT = "#0bbdb7";
const TEAL_DARK = "#057a76";

// ---------------------------------------------------------------------------
// Shared preview chrome (mirrors real widget card + header)
// ---------------------------------------------------------------------------

function PreviewCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
    return (
        <div className="bg-white rounded-[16px] border border-[#c9d1d4] flex flex-col h-full">
            <div className="relative py-6 px-4">
                <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-widget-title ml-4" style={{ fontFamily: "Roboto, sans-serif" }}>
                        {title}
                    </h2>
                    <InfoIcon />
                </div>
                {subtitle ? (
                    <div className="text-xs text-slate-500 leading-4 ml-4" style={{ fontFamily: "Roboto, sans-serif" }}>
                        {subtitle}
                    </div>
                ) : null}
            </div>
            <div className="flex-1 px-4 pb-4 font-roboto min-h-0">{children}</div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Scaled container
// ---------------------------------------------------------------------------

function ScaledWidget({ children, renderWidth = 640, renderHeight = 380 }: { children: React.ReactNode; renderWidth?: number; renderHeight?: number }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0);

    useEffect(() => {
        if (!containerRef.current) return;
        const update = () => {
            if (containerRef.current) {
                setScale(containerRef.current.offsetWidth / renderWidth);
            }
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [renderWidth]);

    return (
        <div ref={containerRef} className="w-full h-full overflow-hidden relative">
            <div
                className="pointer-events-none select-none"
                style={{
                    width: renderWidth,
                    height: renderHeight,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    opacity: scale > 0 ? 1 : 0,
                }}
            >
                {children}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// 1. Ticker Preview
// ---------------------------------------------------------------------------

function TickerPreview({ title }: { title?: string }) {
    const ticker = { value: 281717, label: "Sessions", color: "#4f9a9b" };

    return (
        <ScaledWidget renderWidth={600} renderHeight={200}>
            <div className="bg-white h-full font-roboto px-4 pt-4">
                <CompactTickerCard
                    title={title || ticker.label}
                    data={{ value: ticker.value, changeType: "positive" }}
                    colors={[ticker.color]}
                    hideActions
                    hideInsights
                    disableTrend
                />
            </div>
        </ScaledWidget>
    );
}

// ---------------------------------------------------------------------------
// 2. Table Preview
// ---------------------------------------------------------------------------

function TablePreview({ title, subtitle }: { title?: string; subtitle?: string }) {
    const rows = [
        { month: "April", sessions: 48291, views: 19832, leads: 312 },
        { month: "May", sessions: 52104, views: 21445, leads: 287 },
        { month: "June", sessions: 45872, views: 18109, leads: 341 },
        { month: "July", sessions: 49388, views: 20567, leads: 298 },
        { month: "August", sessions: 53201, views: 22103, leads: 325 },
        { month: "September", sessions: 46861, views: 19623, leads: 318 },
    ];

    return (
        <ScaledWidget renderWidth={600} renderHeight={360}>
            <PreviewCard title={title || "Website Traffic by Month"} subtitle={subtitle}>
                <div className="w-full h-full">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-[#e0e0e0]">
                                <th className="pb-2 text-[12px] font-semibold text-[#5a717a] w-[140px]">Month</th>
                                <th className="pb-2 text-[12px] font-semibold text-[#5a717a] text-right">Sessions</th>
                                <th className="pb-2 text-[12px] font-semibold text-[#5a717a] text-right">Vehicle Views</th>
                                <th className="pb-2 text-[12px] font-semibold text-[#5a717a] text-right">TrueLeads</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => (
                                <tr key={row.month} className={`${i % 2 === 1 ? "bg-[#f8fafa]" : ""} border-b border-[#f0f0f0]`}>
                                    <td className="py-2 text-[12px] text-[#2d3a3f]">{row.month}</td>
                                    <td className="py-2 text-[12px] text-[#2d3a3f] text-right">{row.sessions.toLocaleString()}</td>
                                    <td className="py-2 text-[12px] text-[#2d3a3f] text-right">{row.views.toLocaleString()}</td>
                                    <td className="py-2 text-[12px] text-[#2d3a3f] text-right">{row.leads.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </PreviewCard>
        </ScaledWidget>
    );
}

// ---------------------------------------------------------------------------
// 3. Line Chart Preview
// ---------------------------------------------------------------------------

const LINE_CHART_DATA = {
    metrics: [
        { type: "metric", services: ["seoanalytics"], showName: "Sessions", metricName: "sessions", metricType: "number", category: "performance" },
        { type: "metric", services: ["seoanalytics"], showName: "Vehicle Views", metricName: "views", metricType: "number", category: "performance" },
        { type: "metric", services: ["seoanalytics"], showName: "True Leads", metricName: "leads", metricType: "number", category: "conversion" },
    ],
    data: [
        { segment: "Apr", sessions: 48000, views: 19000, leads: 12000 },
        { segment: "May", sessions: 52000, views: 21000, leads: 14000 },
        { segment: "Jun", sessions: 45000, views: 18000, leads: 11000 },
        { segment: "Jul", sessions: 49000, views: 20000, leads: 13000 },
        { segment: "Aug", sessions: 53000, views: 22000, leads: 15000 },
        { segment: "Sep", sessions: 46000, views: 19000, leads: 12500 },
    ],
};

function LineChartPreview({ title, subtitle }: { title?: string; subtitle?: string }) {
    return (
        <ScaledWidget renderWidth={600} renderHeight={360}>
            <PreviewCard title={title || "Sessions by Channel"} subtitle={subtitle}>
                <LineChart data={LINE_CHART_DATA as any} colors={[TEAL, TEAL_LIGHT, TEAL_DARK]} widgetLevel="client" />
            </PreviewCard>
        </ScaledWidget>
    );
}

// ---------------------------------------------------------------------------
// 4. Donut / Pie Chart Preview
// ---------------------------------------------------------------------------

const DONUT_DATA = {
    data: [
        { name: "Organic Search", value: 129517, color: "#14b8a6" },
        { name: "Direct", value: 98420, color: "#6366f1" },
        { name: "Cross-network", value: 76510, color: "#7dd3c0" },
        { name: "Paid Search", value: 65204, color: "#341cac" },
        { name: "Other", value: 47001, color: "#429795" },
        { name: "Paid Social", value: 33537, color: "#f0b775" },
        { name: "Referral", value: 5853, color: "#f07575" },
    ],
    metadata: {
        centerLabel: "Total Views",
        centerValue: 456042,
    },
};

function PiePreview({ title, subtitle }: { title?: string; subtitle?: string }) {
    return (
        <ScaledWidget renderWidth={600} renderHeight={380}>
            <PreviewCard title={title || "Traffic Distribution"} subtitle={subtitle}>
                <PieChart data={DONUT_DATA as any} donut showLegend={false} showList />
            </PreviewCard>
        </ScaledWidget>
    );
}

// ---------------------------------------------------------------------------
// 5. Funnel Preview
// ---------------------------------------------------------------------------

function FunnelPreview({ title, subtitle }: { title?: string; subtitle?: string }) {
    const stages = [
        { label: "Impressions", value: 473830, metricName: "impressions" },
        { label: "Touchpoints", value: 25501, metricName: "touchpoints" },
        { label: "Clicks", value: 12429, metricName: "clicks" },
        { label: "Leads", value: 3218, metricName: "leads" },
        { label: "Conversions", value: 847, metricName: "conversions" },
    ];

    return (
        <ScaledWidget renderWidth={600} renderHeight={380}>
            <PreviewCard title={title || "Consumer Journey Funnel"} subtitle={subtitle || "Conversion funnel for the last 6 months"}>
                <Funnel data={{ data: stages }} colors={[TEAL, TEAL_LIGHT, "#84c4c4", "#b0dbd9", "#d0edec"]} />
            </PreviewCard>
        </ScaledWidget>
    );
}

// ---------------------------------------------------------------------------
// 6. Bar Chart Preview (horizontal bars)
// ---------------------------------------------------------------------------

const VERTICAL_BAR_DATA = {
    metrics: [
        { type: "metric", services: ["seoanalytics"], showName: "Views", metricName: "value", metricType: "number", category: "performance" },
    ],
    data: [
        { segment: "CLA", value: 1380 },
        { segment: "GLE", value: 1120 },
        { segment: "C-Class", value: 890 },
        { segment: "E-Class", value: 720 },
        { segment: "GLC", value: 650 },
        { segment: "A-Class", value: 480 },
    ],
};

const V_BAR_COLORS = ["#341cac", "#429795", "#84c4c4", "#f0b775", "#f07575", "#6b46c1"];

function BarChartPreview({ title, subtitle }: { title?: string; subtitle?: string }) {
    return (
        <ScaledWidget renderWidth={600} renderHeight={360}>
            <PreviewCard title={title || "Top Audiences"} subtitle={subtitle || "Most viewed vehicle models"}>
                <BarChart data={VERTICAL_BAR_DATA as any} colors={V_BAR_COLORS} />
            </PreviewCard>
        </ScaledWidget>
    );
}

// ---------------------------------------------------------------------------
// 7. Bubble Chart Preview
// ---------------------------------------------------------------------------

const BUBBLE_DATA = {
    data: [
        { label: "Luxury", value: 36741, color: "#14b8a6" },
        { label: "SUV", value: 31241, color: "#6366f1" },
        { label: "Sedan", value: 21417, color: "#7dd3c0" },
        { label: "Coupe", value: 16338, color: "#341cac" },
        { label: "Crossover", value: 8420, color: "#429795" },
        { label: "Hatchback", value: 5188, color: "#f0b775" },
        { label: "Roadster", value: 1188, color: "#f07575" },
    ],
};

function BubbleChartPreview({ title, subtitle }: { title?: string; subtitle?: string }) {
    return (
        <ScaledWidget renderWidth={600} renderHeight={360}>
            <PreviewCard title={title || "Audience Size"} subtitle={subtitle || "Audience segment distribution"}>
                <BubbleChart data={BUBBLE_DATA as any} showLegend />
            </PreviewCard>
        </ScaledWidget>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MiniWidgetPreview({ widgetConfig }: MiniWidgetPreviewProps) {
    const chartType = widgetConfig.chartType || widgetConfig.widgetType;
    const title = widgetConfig.title || undefined;
    const subtitle = widgetConfig.description || undefined;

    switch (chartType) {
        case "ticker":
            return <TickerPreview title={title} />;
        case "table":
            return <TablePreview title={title} subtitle={subtitle} />;
        case "line":
            return <LineChartPreview title={title} subtitle={subtitle} />;
        case "pie":
            return <PiePreview title={title} subtitle={subtitle} />;
        case "funnel":
            return <FunnelPreview title={title} subtitle={subtitle} />;
        case "bar":
            return <BarChartPreview title={title} subtitle={subtitle} />;
        case "bubble":
            return <BubbleChartPreview title={title} subtitle={subtitle} />;
        default:
            return <BarChartPreview title={title} subtitle={subtitle} />;
    }
}
